import { isGivmoError } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import type {
  ComplianceFilingYearBody,
  ComplianceOperations,
  CompliancePage,
} from './compliance-context';

/** The page size used by every module list read. */
export const COMPLIANCE_PAGE_SIZE = 100;

/**
 * The profile read's query key, spelled once because a HOST invalidates it: the viewer's own
 * reminders switch lives on the host's Settings page and moves `reminder_recipients_present`, so the
 * host re-reads the profile after that switch is written (CCM-R1-REMIND-WEB).
 */
export function complianceProfileQueryKey(orgRef: string | null) {
  return ['compliance', 'profile', orgRef] as const;
}

/** A hard stop against a server that never returns a terminal short page. */
const MAX_COMPLIANCE_PAGES = 50;

async function readAllPages(
  read: (page: CompliancePage) => Promise<unknown>,
  what: string,
): Promise<unknown[]> {
  const rows: unknown[] = [];
  for (let pageIndex = 0; pageIndex < MAX_COMPLIANCE_PAGES; pageIndex += 1) {
    const body = await read({
      limit: COMPLIANCE_PAGE_SIZE,
      offset: pageIndex * COMPLIANCE_PAGE_SIZE,
    });
    const page = extractList<unknown>(body);
    rows.push(...page);
    if (page.length < COMPLIANCE_PAGE_SIZE) return rows;
  }
  throw new Error(
    `compliance: ${what} did not terminate within ${MAX_COMPLIANCE_PAGES} pages; refusing to render a truncated register`,
  );
}

export function listAllObligations(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages((page) => operations.listObligations(orgRef, page), 'obligations');
}

export function listAllOccurrences(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages((page) => operations.listOccurrences(orgRef, page), 'occurrences');
}

export function listAllRegistrationImports(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listRegistrationImports(orgRef, page),
    'registration imports',
  );
}

export function listAllArtifacts(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages((page) => operations.listArtifacts(orgRef, page), 'artifacts');
}

export function listAllParameters(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listProfileParameters(orgRef, { ...page, includeSuperseded: true }),
    'profile parameters',
  );
}

export function listAllProfileReviewFlags(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listProfileReviewFlags(orgRef, page),
    'profile review flags',
  );
}

/** Every item of one revision, in the order they are presented (CCM-R1-REVIEW-WEB). */
export function listAllProposalItems(
  operations: ComplianceOperations,
  orgRef: string,
  revisionId: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listProposalItems(orgRef, revisionId, page),
    'proposal items',
  );
}

/** Every ask matching the filter, newest first as served (CCM-R1-REVIEW-ASSIGN; frame 108). */
export function listAllReviewAsks(
  operations: ComplianceOperations,
  orgRef: string,
  filter: { askedUserId?: string; state?: 'open' | 'resolved' | 'withdrawn' | 'replaced' },
): Promise<unknown[]> {
  return readAllPages((page) => operations.listReviewAsks(orgRef, filter, page), 'review asks');
}

/**
 * Every result of one revision: one per rule in scope, more than a page (134 today, nearly all of
 * them `not_supported`), so the count the review states is never a page's.
 */
export function listAllProposalResults(
  operations: ComplianceOperations,
  orgRef: string,
  revisionId: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listProposalResults(orgRef, revisionId, page),
    'proposal results',
  );
}

export function listAllProfileSuggestions(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listProfileSuggestions(orgRef, page),
    'profile suggestions',
  );
}

/**
 * The every-jurisdiction picker read's key, spelled once: the add sheet reads it, and so does the
 * filings composition while no fiscal year end is recorded (CCM-R1-INTAKE-NODATE-FYE).
 */
export function filingOptionsQueryKey(orgRef: string | null) {
  return ['compliance', 'filing-options', orgRef] as const;
}

/** The add sheet's picker, one jurisdiction's filings or every jurisdiction's (CCM-R1-FILING-META). */
export function listAllFilingOptions(
  operations: ComplianceOperations,
  orgRef: string,
  jurisdictionCode?: string,
  lastFilingOn?: string,
  filedFor?: ComplianceFilingYearBody,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listFilingOptions(orgRef, page, jurisdictionCode, lastFilingOn, filedFor),
    'filing options',
  );
}

export function listAllFilingGroups(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages((page) => operations.listFilingGroups(orgRef, page), 'filing groups');
}

/**
 * The organization's registered agents and foreign qualifications, current and ended (CCM-MOD5B):
 * the section on Filings, the Calendar's own dates and the lines naming a library filing read them.
 */
export function listAllVendorRecords(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages((page) => operations.listVendorRecords(orgRef, page), 'vendor records');
}

export function vendorRecordsQueryKey(orgRef: string | null) {
  return ['compliance', 'vendor-records', orgRef] as const;
}

export function vendorRecordQueryKey(orgRef: string | null, vendorId: string) {
  return ['compliance', 'vendor-record', orgRef, vendorId] as const;
}

export function listAllOrganizationFilings(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listOrganizationFilings(orgRef, page),
    'organization filings',
  );
}

/**
 * Every renewal cycle, or one obligation's (CCM-R1-FILING-CYCLES).
 *
 * ⚠ EXHAUSTED like every list here: a year on page 2 would otherwise read "Nothing recorded for this
 * year" over a year the organization recorded.
 */
export function listAllRenewalCycles(
  operations: ComplianceOperations,
  orgRef: string,
  obligationId?: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listRenewalCycles(orgRef, page, obligationId),
    'renewal cycles',
  );
}

/**
 * Each filing case's latest send, or record of sending, with what is known about it so far
 * (CCM-R1-RESULTS-WEB): the panel's, the Calendar's and Filings' lines after a send.
 */
export function listAllSubmissionFollowThrough(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listSubmissionFollowThrough(orgRef, page),
    'submission follow-through',
  );
}

export function submissionFollowThroughQueryKey(orgRef: string | null) {
  return ['compliance', 'submission-follow-through', orgRef] as const;
}

// ── Preparing a filing's form (CCM-R1-DOCS; CCM-R1-DOCS-READS) ──────────────────────────────────
//
// One key per read, so an answer invalidates exactly the preparation (and through its
// `values_sha256`, the pages) and an attachment exactly the checklist.

export function formAdaptersQueryKey(orgRef: string | null) {
  return ['compliance', 'form-adapters', orgRef] as const;
}

export function filingCasesQueryKey(orgRef: string | null, obligationId: string | undefined) {
  return ['compliance', 'filing-cases', orgRef, obligationId] as const;
}

export function casePreparationsQueryKey(orgRef: string | null, caseId: string | undefined) {
  return ['compliance', 'case-preparations', orgRef, caseId] as const;
}

export function formPreparationQueryKey(orgRef: string | null, preparationId: string | undefined) {
  return ['compliance', 'form-preparation', orgRef, preparationId] as const;
}

/** A page as the server draws it for one state of the answers: a new answer is a new key. */
export function formPagePreviewQueryKey(
  orgRef: string | null,
  preparationId: string | undefined,
  page: number,
  valuesSha256: string | undefined,
) {
  return ['compliance', 'form-page', orgRef, preparationId, page, valuesSha256] as const;
}

export function filingChecklistQueryKey(orgRef: string | null, caseId: string | undefined) {
  return ['compliance', 'filing-checklist', orgRef, caseId] as const;
}

export function caseSigningRequestsQueryKey(orgRef: string | null, caseId: string | undefined) {
  return ['compliance', 'case-signing-requests', orgRef, caseId] as const;
}

export function listAllFormAdapters(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown[]> {
  return readAllPages((page) => operations.listFormAdapters(orgRef, page), 'form adapters');
}

export function listAllFilingCases(
  operations: ComplianceOperations,
  orgRef: string,
  obligationId: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listFilingCases(orgRef, obligationId, page),
    'filing cases',
  );
}

export function listAllCasePreparations(
  operations: ComplianceOperations,
  orgRef: string,
  caseId: string,
): Promise<unknown[]> {
  return readAllPages(
    (page) => operations.listCasePreparations(orgRef, caseId, page),
    'form preparations',
  );
}

/**
 * A case's signing requests. Signing is its own dark door: where it is closed the read answers
 * the module's 404, which here reads as "no request" — the form is simply not out for signature.
 */
export async function listAllCaseSigningRequests(
  operations: ComplianceOperations,
  orgRef: string,
  caseId: string,
): Promise<unknown[]> {
  try {
    return await readAllPages(
      (page) => operations.listCaseSigningRequests(orgRef, caseId, page),
      'signing requests',
    );
  } catch (cause) {
    if (isGivmoError(cause) && cause.status === 404) return [];
    throw cause;
  }
}

export function signingRequestQueryKey(orgRef: string | null, requestId: string | undefined) {
  return ['compliance', 'signing-request', orgRef, requestId] as const;
}

// ── Signing a request (CCM-R1-SIGN-WEB, the signer's side) ──────────────────────────────────────

/** A completed request's record lines (B6), read beside the signed form (CCM-R1-SIGN-WEB-B). */
export function signingRecordQueryKey(orgRef: string | null, requestId: string | undefined) {
  return ['compliance', 'signing-record', orgRef, requestId] as const;
}

/** Every request's record lines of the organization: what a removal of sign-in details rewrites. */
export function signingRecordsQueryKey(orgRef: string | null) {
  return ['compliance', 'signing-record', orgRef] as const;
}

/**
 * Everyone the organization's signing requests hold sign-in details for, page by page
 * (CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS (web)).
 */
export function signingPeopleQueryKey(orgRef: string | null) {
  return ['compliance', 'signing-people', orgRef] as const;
}

/** One durable cycle, read to name a request's filing (the signer page; P4). */
export function renewalCycleQueryKey(orgRef: string | null, cycleId: string | undefined) {
  return ['compliance', 'renewal-cycle', orgRef, cycleId] as const;
}

/** One filing (obligation) with its occurrences, read to name a request's filing (P4). */
export function obligationQueryKey(orgRef: string | null, obligationId: string | undefined) {
  return ['compliance', 'obligation', orgRef, obligationId] as const;
}

/** The organization's sign-in check: read fresh before an act, so the prompt names the level in force. */
export function signingSettingQueryKey(orgRef: string | null) {
  return ['compliance', 'signing-setting', orgRef] as const;
}

export function filingCaseQueryKey(orgRef: string | null, caseId: string | undefined) {
  return ['compliance', 'filing-case', orgRef, caseId] as const;
}

export function filingDocumentQueryKey(orgRef: string | null, documentId: string | undefined) {
  return ['compliance', 'filing-document', orgRef, documentId] as const;
}

export function versionRenditionQueryKey(
  orgRef: string | null,
  documentId: string | undefined,
  versionId: string | undefined,
) {
  return ['compliance', 'version-rendition', orgRef, documentId, versionId] as const;
}

// ── Sending a filing (CCM-R1-SUBMIT-WEB) ────────────────────────────────────────────────────────

/** A case's submission: the send review's read, which every act on it answers too. */
export function submissionQueryKey(orgRef: string | null, caseId: string | undefined) {
  return ['compliance', 'submission', orgRef, caseId] as const;
}

/** The organization's agreement for Givmo to file (CCM-R1-DESK-WEB): its standing read. */
export function deskStandingQueryKey(orgRef: string | null) {
  return ['compliance', 'desk-standing', orgRef] as const;
}

/**
 * The organization's agreement for Givmo to file. The desk is its own dark door: where it is closed
 * the read answers the module's 404, which here reads as no agreement, so nothing offers the desk.
 */
export async function getDeskStanding(
  operations: ComplianceOperations,
  orgRef: string,
): Promise<unknown> {
  try {
    return await operations.getDeskStanding(orgRef);
  } catch (cause) {
    if (isGivmoError(cause) && cause.status === 404) return null;
    throw cause;
  }
}

/**
 * A case's submission for the panel. Sending is its own dark door: where it is closed the read
 * answers the module's 404, which here reads as no submission — the panel then offers nothing.
 */
export async function getCaseSubmission(
  operations: ComplianceOperations,
  orgRef: string,
  caseId: string,
): Promise<unknown> {
  try {
    return await operations.getSubmission(orgRef, caseId);
  } catch (cause) {
    if (isGivmoError(cause) && cause.status === 404) return null;
    throw cause;
  }
}
